import { attach, combine, createDomain, sample } from 'effector'
import { FormValues, massMailingModel } from '~/entities/MassMailing'
import { AxiosErrorType } from '~/shared/api'
import { MassMailing } from '~/shared/api/massMailing'
import { MassMailingStatus } from '~/shared/config/enums'
import { replaceFx } from '~/shared/lib/history'
import { logger } from '~/shared/lib/logger'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { isString } from '~/shared/lib/utils'
import { formButtonsModel } from '~/shared/ui/Form/FormButtons'

export const domain = createDomain('features.massMailing.editMassMailing')

export const $massMailing = combine(
  massMailingModel.$massMailingId,
  massMailingModel.$massMailingCache,
  (id, cache) => {
    if (!id) return null
    return cache.map[id] ?? null
  },
)

export const formSubmitted = domain.createEvent<FormValues>()
export const massMailingUpdateFx = attach({
  effect: massMailingModel.saveMassMailingFx,
  source: massMailingModel.$massMailingId,
  mapParams: (values: FormValues, id) => {
    const { userStatuses, regionsIds, ...restAttrs } = values
    const massMailing = new MassMailing(
      {
        ...restAttrs,
        userStatuses: (userStatuses || []).map((status) =>
          isString(status) ? status : status.id,
        ),
      },
      id as string,
    )
    if (Array.isArray(regionsIds)) massMailing.setRegions(regionsIds)

    return massMailing
  },
})

sample({
  clock: formSubmitted,
  target: massMailingUpdateFx,
})

sample({
  clock: massMailingUpdateFx.doneData,
  fn() {
    return {
      message: 'Сохранено успешно',
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued, formButtonsModel.editingEnded],
})

sample({
  clock: massMailingUpdateFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: 'Ошибка при сохранении данных',
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

export const $canEditMassMailing = $massMailing.map(
  (massMailing) => massMailing?.getStatus() === MassMailingStatus.WAITING,
)

export const deleteMassMailing = domain.createEvent()
export const deleteMassMailingFx = domain.createEffect<
  UniqueId,
  void,
  AxiosErrorType
>({
  async handler(massMailingId) {
    const massMailing = new MassMailing({}, massMailingId)
    await massMailing.delete()
  },
})

sample({
  clock: deleteMassMailing,
  source: massMailingModel.$massMailingId,
  filter: isString,
  target: deleteMassMailingFx,
})

sample({
  clock: deleteMassMailingFx.doneData,
  fn() {
    return {
      message: 'Удалено успешно',
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued],
})

sample({
  clock: deleteMassMailingFx.doneData,
  fn: () => '/mass-mailing',
  target: replaceFx,
})

sample({
  clock: deleteMassMailingFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: 'Ошибка при удалении уведомления',
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})
